import React from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  history,
} from 'web-history';

import {
  uiActions,
} from 'web-actions';

import {
  getUiState,
} from 'web-selectors';

import {
  Flex,
  Text,
} from 'web-styled';

import {
  data,
} from 'web-utils';

import {
  Button,
  InviteClient,
} from 'web-components';

import * as S from './styled';

const getHeightString = (value: any, from: string, to: string) => {
  if (from === 'imperial' && to === 'imperial') {
    return data.inchesToFeetInches(value);
  }

  return data.heightConvert(value, from, to);
};

const yearsAgo = (str: string) => Math.floor(
  Math.abs(
    DateTime.fromFormat(str, 'yyyy-mm-dd').diffNow('years').years,
  ),
);

const mapState = state => ({
  me: coreSelectors.getProfile(state),
  ...getUiState([
    'clientAvatarUrl',
    'inviteClientModal',
  ])(state),
});

type Props = {
  id: string,
  clientInfo: any,
};

const Header = ({ id, clientInfo }: Props) => {
  const dispatch = useDispatch();

  const {
    me,
    clientAvatarUrl,
    inviteClientModal,
  } = useSelector(mapState, shallowEqual);
  const profile = clientInfo.user_profile;
  const units = me.user_settings.units_system;
  const clientUnits = clientInfo.user_settings.units_system;

  const setInviteClientModal = (isOpen: boolean) => {
    dispatch(
      uiActions.setUiState({
        inviteClientModal: isOpen,
      }),
    );
  };

  return (
    <Flex
      column
      width="100%"
      spacing={10}
    >
      <S.ClientInfo>
        <Flex
          spacing={25}
          padding="20px"
          justify="space-between"
        >
          <Flex>
            <S.StyledAvatar
              src={clientAvatarUrl || profile?.avatar?.large?.url}
            />
            <Flex
              justify="center"
              padding="10px 0 0 30px"
              spacing={20}
              column
            >
              <Flex
                spacing={5}
                column
              >
                <Text
                  size="22px"
                  color="#fff"
                  weight={800}
                >
                  {`${profile.first_name} ${profile.last_name}`}
                </Text>
                <Text
                  size="13px"
                  color="#fff"
                  weight={500}
                >
                  {`${profile.location || ''} ${profile.zip || ''}`}
                </Text>
              </Flex>
              <Flex
                spacing={10}
              >
                <Button
                  ghost
                  appearance="primaryReverse"
                  spacing="middle"
                  alignSelf="center"
                  onClick={() => setInviteClientModal(true)}
                >
                  {i18n.t('invite.client')}
                </Button>
                <Button
                  ghost
                  appearance="primaryReverse"
                  spacing="middle"
                  alignSelf="center"
                  onClick={() => {
                    history.push(`/users/${id}/edit`);
                  }}
                  isDisabled={!me.is_pro && (me.id !== clientInfo.id)}
                >
                  Edit Profile
                </Button>
              </Flex>
            </Flex>
          </Flex>
          <S.ClientSummary>
            <tbody>
              <tr>
                <td>
                  {`height (${units === 'imperial' ? 'ft\' in' : 'cm'})`}
                </td>
                <td>
                  {profile.height
                    ? getHeightString(profile.height, clientUnits, units)
                    : '-'}
                </td>
              </tr>
              <tr>
                <td>
                  {`weight (${units === 'imperial' ? 'lbs' : 'kg'})`}
                </td>
                <td>
                  {profile.weight
                    ? Math.round(data.weightConvert(profile.weight, clientUnits, units))
                    : '-'}
                </td>
              </tr>
              <tr>
                <td>
                  bodyfat (%)
                </td>
                <td>
                  {(profile.bodyfat || '-')}
                </td>
              </tr>
              <tr>
                <td>
                  age
                </td>
                <td>
                  {profile.birthday ? yearsAgo(profile.birthday) : '-'}
                </td>
              </tr>
              <tr>
                <td>
                  gender
                </td>
                <td>
                  {(profile.gender || '-')}
                </td>
              </tr>
            </tbody>
          </S.ClientSummary>
        </Flex>
      </S.ClientInfo>
      <InviteClient
        label={i18n.t('invite.client')}
        buttonLabel="Invite"
        email={clientInfo.email}
        isOpen={inviteClientModal}
        onRequestClose={() => {
          setInviteClientModal(false);
        }}
        onSubmit={(values, actions) => {
          dispatch(
            coreActions.requestInviteClient({
              userId: clientInfo.id,
              payload: values,
              callbacks: {
                ...actions,
                onSuccess: () => {
                  setInviteClientModal(false);
                },
              },
            }),
          );
        }}
      />
    </Flex>
  );
};

export default Header;
